
import { useState } from 'react';
import { Link } from 'react-router-dom';
import LeadsHero from './components/LeadsHero';
import CategoryGrid from './components/CategoryGrid';
import HowItWorks from './components/HowItWorks';
import LeadPricing from './components/LeadPricing';
import RequestQuoteForm from './components/RequestQuoteForm';
import { leadCategories } from '../../mocks/leadCategories';
import { useSEO, generateBreadcrumbSchema } from '../../utils/seo';

export default function LeadsPage() {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showQuoteForm, setShowQuoteForm] = useState(false);

  useSEO({
    title: 'Get Local Service Quotes in Roseville CA | Leads Marketplace | Roseville.org',
    description: 'Request quotes from verified local service providers in Roseville, California. Home improvement, plumbing, electrical, landscaping, and more. Connect with 350+ trusted Roseville contractors and service professionals.',
    keywords: 'Roseville service quotes, local contractors Roseville CA, home services Roseville, Roseville leads marketplace, find service providers Roseville',
    canonical: '/leads',
    ogImage: 'https://public.readdy.ai/ai/img_res/44e170112dd02cbecdca3abb34e625df.jpg',
    schema: {
      '@context': 'https://schema.org',
      '@graph': [
        {
          '@type': 'WebPage',
          name: 'Local Service Leads Marketplace — Roseville, CA',
          description: 'Connect with verified local service providers and contractors in Roseville, California.',
          url: `${import.meta.env.VITE_SITE_URL || 'https://roseville.org'}/leads`,
          inLanguage: 'en-US',
        },
        generateBreadcrumbSchema([
          { name: 'Home', url: '/' },
          { name: 'Local Services', url: '/leads' },
        ]),
        {
          '@type': 'Service',
          name: 'Roseville Local Service Leads',
          description: 'Marketplace connecting Roseville residents with verified local service providers.',
          provider: {
            '@type': 'Organization',
            name: 'Roseville.org',
            url: import.meta.env.VITE_SITE_URL || 'https://roseville.org',
          },
          areaServed: { '@type': 'City', name: 'Roseville', addressRegion: 'CA' },
        },
      ],
    },
  });

  const handleCategorySelect = (categoryId: string) => {
    setSelectedCategory(categoryId);
    setShowQuoteForm(true);
  };

  const handleCloseForm = () => {
    setShowQuoteForm(false);
    setSelectedCategory(null);
  };

  return (
    <div className="min-h-screen bg-white">
      <LeadsHero />
      
      <CategoryGrid 
        categories={leadCategories}
        onCategorySelect={handleCategorySelect}
      />
      
      <HowItWorks />
      
      <LeadPricing />
      
      {/* Business CTA Section */}
      <section className="py-16 bg-gradient-to-br from-emerald-50 to-teal-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-white rounded-2xl shadow-lg p-12 text-center">
            <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <i className="ri-briefcase-line text-3xl text-white"></i>
            </div>
            <h2 className="text-3xl font-bold text-gray-900 mb-4">
              Are You a Service Provider?
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Join our network and receive qualified leads from customers actively looking for your services. Pay only for the leads you want.
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <Link
                to="/lead-dashboard"
                className="px-8 py-4 bg-emerald-500 text-white rounded-lg font-semibold hover:bg-emerald-600 transition-colors whitespace-nowrap"
              >
                View Lead Dashboard
              </Link>
              <Link
                to="/submit-business"
                className="px-8 py-4 bg-white text-emerald-500 border-2 border-emerald-500 rounded-lg font-semibold hover:bg-emerald-50 transition-colors whitespace-nowrap"
              >
                Register Your Business
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Indicators */}
      <section className="py-12 bg-white border-t border-gray-100">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl font-bold text-emerald-500 mb-2">2,500+</div>
              <div className="text-sm text-gray-600">Leads Generated</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-500 mb-2">350+</div>
              <div className="text-sm text-gray-600">Service Providers</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-500 mb-2">4.8/5</div>
              <div className="text-sm text-gray-600">Average Rating</div>
            </div>
            <div>
              <div className="text-3xl font-bold text-emerald-500 mb-2">24hrs</div>
              <div className="text-sm text-gray-600">Average Response</div>
            </div>
          </div>
        </div>
      </section>

      {/* Quote Form Modal */}
      {showQuoteForm && selectedCategory && (
        <RequestQuoteForm
          categoryId={selectedCategory}
          onClose={handleCloseForm}
        />
      )}
    </div>
  );
}
